import { type MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { addActionNameToMutationResult } from 'shared/hooks/utils/add-action-name-to-mutation-result';
import { useAxiosInstance } from 'shared/hooks/utils/axios-instance';
import type { ApiError } from 'shared/hooks/utils/types';
import { z } from 'zod/v4';

const UpdateDraftSchema = z.object({
  draftId: z.string(),
  threadId: z.string(),
  subject: z.string(),
  body: z.string(),
  toMail: z.array(z.string()),
  ccMail: z.array(z.string()).optional().default([]),
  bccMail: z.array(z.string()).optional().default([]),
});

export type UpdateDraftVariables = z.infer<typeof UpdateDraftSchema>;

const UpdateDraftResponseSchema = z.object({
  subject: z.string(),
  body: z.string(),
  toMail: z.array(z.string()),
  ccMail: z.array(z.string()).optional(),
  bccMail: z.array(z.string()).optional(),
});

type UpdateDraftResponse = z.infer<typeof UpdateDraftResponseSchema>;

async function updateDraftFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateDraftVariables,
): Promise<UpdateDraftResponse> {
  const { draftId, threadId, ...payload } = UpdateDraftSchema.parse(variables);
  const instance = await axiosInstance;
  const response = await instance.put(`mails/drafts/${draftId}`, payload);
  return UpdateDraftResponseSchema.parse(response.data);
}

export function useUpdateDraft(
  options: MutationOptions<UpdateDraftResponse, ApiError, UpdateDraftVariables> = {},
) {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<UpdateDraftResponse, ApiError, UpdateDraftVariables>({
    mutationFn: variables => updateDraftFn(axiosInstance, variables),
    onSuccess: (_, variables) => {
      client.invalidateQueries({ queryKey: ['mail', variables.threadId] });
      client.invalidateQueries({ queryKey: ['mails'] });
    },
    ...options,
  });

  return addActionNameToMutationResult<
    UpdateDraftResponse,
    ApiError,
    UpdateDraftVariables,
    'updateDraft'
  >('updateDraft', mutation);
}
